import React, { lazy } from 'react';
import { TopbarNavigationMFEProps, TopbarNavigationSkeleton } from '@cw-elements/host-kit';
import { loadRemoteModule } from '@cw-elements/mfe-setup/load';
import ConnectLogo from 'assets/cropwise_connect_logo.svg';
import i18n from 'services/localization/config';
import { availableLanguages, fixLang } from './languages';
import dayjs from 'dayjs';
import { setStorage } from 'models/Browser';
import { AvailableUnits } from '@cw-elements/host-kit/topbar-navigation/topbar-navigation.model';
import {
  UNIT_SYSTEM_STORAGE_KEY,
  UnitSystemTypes,
  useUnitSystemStore,
} from 'modules/yield-analysis/store/useUnitSystemStore';
import './styles.less';

const TopbarNavigation = lazy(() =>
  loadRemoteModule<TopbarNavigationMFEProps>('topbar-navigation/index'),
);

export type TopNavBarV2Props = {
  logoutHandler: () => void;
  selectOrg: (orgId: string) => void;
  selectedOrgId?: string;
  selectedWorkspaceId?: string;
  changeUnitSystem: (unit: string) => void;
};

const TopNavBarV2 = ({
  logoutHandler,
  selectOrg,
  selectedOrgId,
  selectedWorkspaceId,
  changeUnitSystem,
}: TopNavBarV2Props) => {
  const { setUnitSystem, unitSystem } = useUnitSystemStore();
  const settingsMenuConfig = () => ({
    headerSelector: '#topbar-navigation',
  });

  const handleChangeLanguage = (language: string) => {
    language = fixLang(language);
    i18n.changeLanguage(language);
    dayjs.locale(language.replace('_', '-'));
    localStorage.setItem('language', language);
  };

  const handleChangeUnitSystem = (unitSystem: string) => {
    changeUnitSystem(unitSystem.toUpperCase());
    setStorage(UNIT_SYSTEM_STORAGE_KEY, unitSystem.toUpperCase());
    setUnitSystem(unitSystem.toUpperCase() as UnitSystemTypes);
  };

  const profileDropdownConfig = () => ({
    availableLanguages: Object.values(availableLanguages),
    onLanguageChange: (language: string) => handleChangeLanguage(language),
    defaultLanguage: i18n.language,
    availableUnitSystems: ['metric', 'imperial'] as AvailableUnits[],
    onUnitSystemChange: (unitSystem: string) => handleChangeUnitSystem(unitSystem),
    defaultUnitSystem: unitSystem.toLowerCase() as AvailableUnits,
  });

  return (
    <React.Suspense
      fallback={
        <TopbarNavigationSkeleton
          featureConfig={{
            hasSettingsMenu: true,
            hasProfileDropdown: true,
            hasDeckOfApps: true,
          }}
        />
      }
    >
      <TopbarNavigation
        userLanguage={i18n.language.replace('_', '-')}
        onLogout={logoutHandler}
        productImage={{
          redirectUrl: '/',
          imageSrc: ConnectLogo,
          name: 'Connect',
        }}
        featureConfig={{
          hasDeckOfApps: true,
          hasSettingsMenu: true,
          hasProfileDropdown: true,
        }}
        defaultItems={{
          selectedOrganization: { value: selectedOrgId ?? '' },
          selectedWorkspace: { value: selectedWorkspaceId ?? '' },
        }}
        organizationDropdown={() => ({
          onSelect: (item) => {
            selectOrg(item.value);
          },
        })}
        settingsMenuConfig={settingsMenuConfig}
        profileDropdownConfig={profileDropdownConfig}
        entitiesFilter={{ exclude_farms: true }}
      />
    </React.Suspense>
  );
};

export default TopNavBarV2;
